import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import StatusBadge from "../components/StatusBadge.jsx";
import Dropdown from "../components/Dropdown.jsx";
import "../styles/OfficerSubmission.css";

const SUGGEST_DEBOUNCE_MS = 2500;
const SUGGEST_MIN_CHARS = 25;

const DOC_TYPES = [
  "LETTER", "PHOTO", "WORK_ORDER", "BILL",
  "MEASUREMENT", "PROCEEDINGS", "RESOLUTION", "OTHER",
];

function fmtDate(d) {
  return d ? new Date(d).toLocaleString("en-IN") : "—";
}

// An officer's submission desk: pick one of their paras by Pathi No, write a
// reply, attach evidence, submit. Past submissions list below as cards that
// expand to show the reply text and the evidence files.
export default function OfficerSubmission() {
  const { user } = useAuth();

  const [paras, setParas] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedPara, setSelectedPara] = useState("");
  const [explanation, setExplanation] = useState("");
  const [docType, setDocType] = useState("LETTER");
  const [files, setFiles] = useState([]);
  const [evDesc, setEvDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [improving, setImproving] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiLang, setAiLang] = useState("English");
  const [translating, setTranslating] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestTimerRef = useRef(null);
  const lastSuggestForRef = useRef("");
  const textareaRef = useRef(null);

  const loadSubmissions = useCallback(() => {
    api
      .get("/replies/", { params: { officer: user.id, page_size: 100 } })
      .then((r) => setSubmissions(r.data.results || r.data))
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    api
      .get("/paras/", { params: { mine: "true", page_size: 100 } })
      .then((r) => setParas(r.data.results || r.data))
      .catch(() => {});
    loadSubmissions();
  }, [loadSubmissions]);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3500);
  };

  // Generate an AI draft reply for the selected para using Gemini 3 Flash.
  const generateAIDraft = async () => {
    if (!selectedPara) {
      setAiError("Select a para first.");
      return;
    }
    setAiError("");
    setAiBusy(true);
    try {
      const r = await api.post("/ai/draft-reply/", {
        para: Number(selectedPara),
        language: aiLang,
        tone: "formal",
        user_input: explanation.trim(),
      });
      setExplanation(r.data.draft);
      flash("AI draft generated. Review and edit as needed.");
    } catch (e) {
      setAiError(
        e.response?.data?.detail || "Could not generate draft. Try again later."
      );
    } finally {
      setAiBusy(false);
    }
  };

  // Rewrite the officer's current draft into a polished formal reply.
  const improveAIDraft = async () => {
    if (!selectedPara) {
      setAiError("Select a para first.");
      return;
    }
    if (!explanation.trim()) {
      setAiError("Write some text first, then click Improve.");
      return;
    }
    setAiError("");
    setImproving(true);
    try {
      const r = await api.post("/ai/improve-reply/", {
        para: Number(selectedPara),
        text: explanation,
        language: aiLang,
      });
      setExplanation(r.data.draft);
      setSuggestion("");
      flash("AI improved your draft.");
    } catch (e) {
      setAiError(
        e.response?.data?.detail || "Could not improve draft. Try again later."
      );
    } finally {
      setImproving(false);
    }
  };

  // Fetch an inline ghost-text continuation. Called debounced from onChange.
  const fetchSuggestion = useCallback(
    async (text) => {
      if (!selectedPara) return;
      if (!text || text.trim().length < SUGGEST_MIN_CHARS) {
        setSuggestion("");
        return;
      }
      // Don't refetch if text hasn't materially changed since last fetch
      if (lastSuggestForRef.current === text) return;
      lastSuggestForRef.current = text;
      setSuggestLoading(true);
      try {
        const r = await api.post("/ai/suggest-reply/", {
          para: Number(selectedPara),
          text,
          language: aiLang,
        });
        // Only apply if the textarea still matches what we sent
        if (lastSuggestForRef.current === text) {
          setSuggestion((r.data.suggestion || "").trim());
        }
      } catch {
        setSuggestion("");
      } finally {
        setSuggestLoading(false);
      }
    },
    [selectedPara, aiLang]
  );

  // Debounced wrapper for textarea onChange.
  const onExplanationChange = (e) => {
    const value = e.target.value;
    setExplanation(value);
    setSuggestion(""); // clear stale suggestion while typing
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    suggestTimerRef.current = setTimeout(() => {
      fetchSuggestion(value);
    }, SUGGEST_DEBOUNCE_MS);
  };

  // Tab to accept ghost text, Esc to dismiss.
  const onExplanationKeyDown = (e) => {
    if (e.key === "Tab" && suggestion) {
      e.preventDefault();
      const sep = explanation.endsWith(" ") || explanation.endsWith("\n") ? "" : " ";
      setExplanation(explanation + sep + suggestion);
      setSuggestion("");
      lastSuggestForRef.current = "";
    } else if (e.key === "Escape" && suggestion) {
      e.preventDefault();
      setSuggestion("");
    }
  };

  useEffect(() => {
    return () => {
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    };
  }, []);

  const translateText = async () => {
    if (!explanation.trim()) {
      setAiError("Write or generate some text first.");
      return;
    }
    setAiError("");
    setTranslating(true);
    try {
      const r = await api.post("/ai/translate-text/", {
        text: explanation,
      });
      setExplanation(r.data.translated);
      flash("Text translated successfully.");
    } catch (e) {
      setAiError(
        e.response?.data?.detail || "Could not translate. Try again later."
      );
    } finally {
      setTranslating(false);
    }
  };

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const handlePrint = () => {
    if (!selectedPara) {
      setError("Select a para first.");
      return;
    }
    if (!explanation.trim()) {
      setError("Write your reply before printing.");
      return;
    }
    const para = paras.find((p) => String(p.id) === String(selectedPara));
    if (!para) return;

    // Strip a trailing Tamil signature block from the reply so it doesn't
    // duplicate the right-aligned signature added below.
    const stripTrailingSignature = (text) => {
      const sigPatterns = [
        "தங்கள் உண்மையுள்ள",
        "பொருளான அலுவலர்",
        "[நகராட்சி பெயர்]",
      ];
      let lines = text.split("\n");
      while (lines.length > 0) {
        const last = lines[lines.length - 1].trim();
        if (last === "" || sigPatterns.some((p) => last.includes(p))) {
          lines.pop();
        } else {
          break;
        }
      }
      return lines.join("\n").trimEnd();
    };
    const replyText = stripTrailingSignature(explanation);

    const evidenceItems = files.map((f) => {
      const url = URL.createObjectURL(f);
      const isImage = f.type.startsWith("image/");
      const isPdf = f.type === "application/pdf";
      return { url, name: f.name, type: f.type, isImage, isPdf };
    });

    const evidenceHtml = evidenceItems.length === 0
      ? `<div class="muted">No evidence attached.</div>`
      : evidenceItems.map((ev, i) => {
          const header = `<div class="ev-head"><strong>${i + 1}. ${escapeHtml(docType.replace(/_/g, " "))}</strong></div>`;
          let body = "";
          if (ev.isImage) {
            body = `<img src="${ev.url}" class="ev-img" alt="" />`;
          } else if (ev.isPdf) {
            body = `<iframe src="${ev.url}" class="ev-pdf"></iframe>`;
          } else {
            body = `<div class="muted">Attached file (${escapeHtml(ev.type || "unknown type")})</div>`;
          }
          return `<div class="ev-block">${header}${body}</div>`;
        }).join("");

    const remarksHtml = evDesc
      ? `<div class="section"><div class="section-title">Evidence Remarks</div><p>${escapeHtml(evDesc)}</p></div>`
      : "";

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Audit Para Reply — ${escapeHtml(para.para_no)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Inter, Arial, sans-serif; color: #0f172a; margin: 24px; line-height: 1.5; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 18px; }
  .doc-header h1 { font-size: 22px; margin: 0 0 4px; }
  .doc-header .header-meta { color: #64748b; font-size: 13px; margin: 0; border: none; padding: 0; }
  .doc-date { text-align: right; font-size: 12px; color: #0f172a; }
  .doc-date .dd-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin-bottom: 2px; }
  .doc-date .dd-value { font-weight: 700; font-size: 13px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .header-meta { color: #64748b; font-size: 13px; margin-bottom: 18px; border-bottom: 2px solid #1e3a8a; padding-bottom: 12px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin: 14px 0; }
  .field-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; }
  .field-value { font-size: 14px; font-weight: 600; color: #0f172a; }
  .section { margin: 20px 0; }
  .section-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #1e3a8a; margin-bottom: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; break-after: avoid; page-break-after: avoid; }
  p { margin: 4px 0; white-space: pre-wrap; }
  .muted { color: #64748b; font-size: 13px; }
  .ev-block { margin: 12px 0 18px; page-break-inside: avoid; }
  .ev-head { font-size: 13px; margin-bottom: 6px; }
  .ev-img { max-width: 100%; max-height: 600px; border: 1px solid #e5e7eb; border-radius: 4px; }
  .ev-pdf { width: 100%; height: 600px; border: 1px solid #e5e7eb; border-radius: 4px; }
  .page-one { display: flex; flex-direction: column; min-height: calc(100vh - 28mm); }
  .signature { margin-top: auto; padding-top: 60px; text-align: right; font-family: "Noto Sans Tamil", "Latha", Arial, sans-serif; font-size: 14px; line-height: 1.9; color: #0f172a; page-break-inside: avoid; page-break-after: always; break-after: page; }
  .signature .sig-close { margin-bottom: 24px; }
  .evidence-section { page-break-before: always; break-before: page; }
  @page { size: A4; margin: 0; }
  @media print {
    html, body { margin: 0; padding: 0; }
    body { padding: 14mm 12mm; }
    .section { padding-top: 6mm; }
    .ev-block { padding-top: 6mm; }
  }
</style>
</head>
<body>
  <div class="page-one">
  <div class="doc-header">
    <div>
      <h1>Audit Para Reply</h1>
      <div class="header-meta">CCMC Corporation · Submitted by ${escapeHtml(user.full_name || "")}</div>
    </div>
    <div class="doc-date">
      <div class="dd-label">Date</div>
      <div class="dd-value">${escapeHtml(new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }))}</div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Para Details</div>
    <div class="grid">
      <div><div class="field-label">Para Number</div><div class="field-value">${escapeHtml(para.para_no)}</div></div>
      <div><div class="field-label">Audit Year</div><div class="field-value">${escapeHtml(para.audit_year)}</div></div>
      <div><div class="field-label">Department</div><div class="field-value">${escapeHtml(para.department || "—")}</div></div>
      <div><div class="field-label">Amount</div><div class="field-value">${para.amount ? "₹" + Number(para.amount).toLocaleString("en-IN") : "—"}</div></div>
    </div>
    <div class="field-label">Title</div>
    <p>${escapeHtml(para.title || "—")}</p>
    <div class="field-label" style="margin-top:10px">Description</div>
    <p>${escapeHtml(para.description || "—")}</p>
  </div>

  <div class="section">
    <div class="section-title">Reply / Explanation</div>
    <p>${escapeHtml(replyText)}</p>
  </div>

  ${remarksHtml}

  <div class="signature">
    <div class="sig-close">தங்கள் உண்மையுள்ள,</div>
    <div>பொருளான அலுவலர்,</div>
    <div>[நகராட்சி பெயர்]</div>
  </div>
  </div>

  <div class="section evidence-section">
    <div class="section-title">Evidence</div>
    ${evidenceHtml}
  </div>

</body>
</html>`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };

    iframe.onload = () => {
      const cw = iframe.contentWindow;
      if (!cw) { cleanup(); return; }
      const triggerPrint = () => {
        try {
          cw.focus();
          cw.print();
        } catch (e) {
          console.error("Print failed", e);
        }
        cleanup();
      };
      const imgs = cw.document.images;
      if (!imgs || imgs.length === 0) {
        setTimeout(triggerPrint, 200);
        return;
      }
      let loaded = 0;
      const onDone = () => { loaded++; if (loaded >= imgs.length) triggerPrint(); };
      for (const img of imgs) {
        if (img.complete) onDone();
        else { img.addEventListener("load", onDone); img.addEventListener("error", onDone); }
      }
    };

    iframe.srcdoc = html;
  };

  const submit = async () => {
    setError("");
    if (!selectedPara) return setError("Select a para first.");
    if (!explanation.trim()) return setError("Write your reply.");
    setBusy(true);
    try {
      // 1. Submit the reply for the chosen para.
      const r = await api.post(`/paras/${selectedPara}/submit_reply/`, {
        explanation: explanation.trim(),
      });
      const replyId = r.data.id;
      // 2. Upload each evidence file, linked to that reply.
      for (const file of files) {
        const fd = new FormData();
        fd.append("para", selectedPara);
        fd.append("reply", replyId);
        fd.append("file", file);
        fd.append("doc_type", docType);
        fd.append("description", evDesc);
        await api.post("/evidence/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setSelectedPara("");
      setExplanation("");
      setFiles([]);
      setEvDesc("");
      setDocType("LETTER");
      flash("Submitted successfully.");
      loadSubmissions();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          e.response?.data?.explanation ||
          "Could not submit. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-submission">
      {msg && (
        <div
          className="badge green"
          style={{ margin: "0 0 12px", display: "block" }}
        >
          {msg}
        </div>
      )}

      {/* New submission */}
      <div className="card">
        <h3>New Submission</h3>
        {error && <div className="error-text">{error}</div>}

        <div className="field">
          <label>Select Para (Pathi Number)</label>
          <Dropdown
            value={String(selectedPara || "")}
            onChange={(v) => setSelectedPara(v)}
            placeholder="— Select a para —"
            options={[
              { value: "", label: "— Select a para —" },
              ...paras.map((p) => {
                const blocked = submissions.some(
                  (s) => String(s.para) === String(p.id) &&
                    (s.review_status === "SUBMITTED" || s.review_status === "ACCEPTED")
                );
                return {
                  value: String(p.id),
                  label: `${p.para_no} · ${p.audit_year} · ${p.title}${blocked ? " — already submitted" : ""}`,
                };
              }),
            ]}
          />
        </div>

        <div className="field">
          <label>Reply / Explanation</label>
          <div className="ai-textarea-wrap" style={{ position: "relative" }}>
            <textarea
              ref={textareaRef}
              value={explanation}
              onChange={onExplanationChange}
              onKeyDown={onExplanationKeyDown}
              placeholder="Write your explanation for this para… or use the AI assistant below."
              data-testid="reply-textarea"
              style={{ minHeight: "180px", resize: "vertical", width: "100%" }}
              rows={8}
              disabled={aiBusy || improving}
            />
            {(aiBusy || improving) && (
              <div className="ai-loader-overlay" data-testid="ai-loader">
                <div className="ai-loader-spinner" />
                <div className="ai-loader-text">
                  {aiBusy ? "✨ Generating your draft…" : "✨ Improving your draft…"}
                </div>
                <div className="ai-loader-sub">Gemini is thinking, this usually takes 2–5 seconds</div>
              </div>
            )}
            {suggestion && (
              <div
                className="ai-ghost-hint"
                data-testid="ai-ghost-suggestion"
                style={{
                  marginTop: 4,
                  padding: "8px 10px",
                  background: "#f5f7fa",
                  border: "1px dashed #c5cbd3",
                  borderRadius: 6,
                  fontSize: 13,
                  color: "#555",
                }}
              >
                <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
                  ✨ Suggestion — press <kbd>Tab</kbd> to accept, <kbd>Esc</kbd> to dismiss
                </div>
                <div style={{ fontStyle: "italic" }}>{suggestion}</div>
              </div>
            )}
            {suggestLoading && !suggestion && (
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                ✨ Thinking of a suggestion…
              </div>
            )}
          </div>
          {/* AI Assistant */}
          <div className="ai-assistant" data-testid="ai-assistant">
            <span className="ai-icon">✨</span>
            <span className="ai-label">AI Assist:</span>
            <Dropdown
              value={aiLang}
              onChange={(v) => setAiLang(v)}
              placeholder="Language"
              options={[
                { value: "English", label: "English" },
                { value: "Tamil", label: "Tamil" },
              ]}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={aiBusy || !selectedPara}
              onClick={generateAIDraft}
              data-testid="generate-ai-draft-btn"
            >
              {aiBusy ? (
                <><span className="ai-btn-spinner" />Generating…</>
              ) : "✨ Generate Draft"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={improving || !selectedPara || !explanation.trim()}
              onClick={improveAIDraft}
              style={{ marginLeft: 8 }}
              data-testid="improve-ai-draft-btn"
            >
              {improving ? (
                <><span className="ai-btn-spinner" />Improving…</>
              ) : "✨ Improve my draft"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={translating || !explanation.trim()}
              onClick={translateText}
              style={{ marginLeft: 8 }}
              data-testid="translate-btn"
            >
              {translating ? (
                <><span className="ai-btn-spinner" />Translating…</>
              ) : "🌐 Translate"}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={!explanation}
              onClick={() => { setExplanation(""); setSuggestion(""); }}
              style={{ marginLeft: 8 }}
              data-testid="clear-btn"
            >
              🗑️ Clear
            </button>
            {aiError && (
              <span style={{ color: "var(--red)", fontSize: 12 }} data-testid="ai-error">
                {aiError}
              </span>
            )}
          </div>
        </div>

        <div className="field">
          <label>Evidence Files (optional)</label>
          <label className="file-upload">
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files))}
            />
            <span className="file-upload-icon">
              <i className="ti ti-cloud-upload"></i>
            </span>
            <span className="file-upload-text">
              {files.length > 0 ? (
                <>
                  <strong>{files.length}</strong> file{files.length > 1 ? "s" : ""} selected
                  <small>Tap to change</small>
                </>
              ) : (
                <>
                  <strong>Tap to choose files</strong>
                  <small>or drag &amp; drop here</small>
                </>
              )}
            </span>
          </label>
        </div>

        <div className="row" style={{ alignItems: "flex-start" }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Evidence Type</label>
            <Dropdown
              value={docType}
              onChange={(v) => setDocType(v)}
              placeholder="Evidence Type"
              options={DOC_TYPES.map((d) => ({
                value: d,
                label: d.replace(/_/g, " "),
              }))}
            />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Remarks (optional)</label>
            <input value={evDesc} onChange={(e) => setEvDesc(e.target.value)} />
          </div>
        </div>

        {(() => {
          const existing = submissions.find(
            (s) => String(s.para) === String(selectedPara) &&
              (s.review_status === "SUBMITTED" || s.review_status === "ACCEPTED")
          );
          return existing ? (
            <div
              className="muted"
              style={{
                marginTop: 12, padding: 10,
                background: "#fff7ed", border: "1px solid #fdba74",
                borderRadius: 6, color: "#9a3412", fontSize: 13,
              }}
            >
              You have already submitted a reply for this para
              {existing.review_status === "ACCEPTED" ? " and it was accepted" : " (awaiting review)"}.
              You cannot submit again unless it is rejected or clarification is requested.
            </div>
          ) : null;
        })()}
        <div className="submit-actions" style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            className="btn btn-primary submit-btn-main"
            disabled={
              busy || !selectedPara || !explanation.trim() ||
              submissions.some(
                (s) => String(s.para) === String(selectedPara) &&
                  (s.review_status === "SUBMITTED" || s.review_status === "ACCEPTED")
              )
            }
            onClick={submit}
          >
            {busy ? "Submitting…" : "Submit Reply & Evidence"}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={!selectedPara || !explanation.trim()}
            onClick={handlePrint}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Past submissions — click a card to see the reply + evidence */}
      <div className="card">
        <h3>My Submissions</h3>
        {submissions.length === 0 ? (
          <div className="muted">No submissions yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {submissions.map((s) => {
              const open = expanded === s.id;
              return (
                <div
                  key={s.id}
                  className="card"
                  style={{ background: "#fafbfc", margin: 0 }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      cursor: "pointer",
                    }}
                    onClick={() => setExpanded(open ? null : s.id)}
                  >
                    <div>
                      <strong>{s.para_no}</strong> · {s.para_audit_year}
                      <div className="muted" style={{ fontSize: 13 }}>
                        {s.para_title}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <StatusBadge status={s.review_status} />
                      <div
                        className="muted"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        {fmtDate(s.submitted_at)}
                      </div>
                    </div>
                  </div>

                  {open && (
                    <div
                      style={{
                        marginTop: 10,
                        borderTop: "1px solid #eee",
                        paddingTop: 10,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>Reply</div>
                      <p style={{ margin: "4px 0 12px", color: "var(--text)" }}>{s.explanation}</p>

                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                        Evidence
                      </div>
                      {s.evidence.length === 0 ? (
                        <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                          No evidence uploaded.
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                          {s.evidence.map((e) => {
                            const fileName = e.file ? decodeURIComponent(e.file.split("/").pop()) : "Evidence";
                            return (
                              <div
                                key={e.id}
                                style={{
                                  padding: "10px 12px",
                                  background: "#ffffff",
                                  border: "1px solid var(--border, #e2e6eb)",
                                  borderRadius: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 10,
                                  }}
                                >
                                  <div style={{ fontSize: 13, color: "var(--text)" }}>
                                    <strong>{e.doc_type_display}</strong> — {fileName}
                                  </div>
                                  <a
                                    href={e.file}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-sm btn-outline"
                                    style={{
                                      textDecoration: "none",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      height: "auto",
                                      display: "inline-flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    View Document
                                  </a>
                                </div>
                                {e.description && (
                                  <div
                                    style={{
                                      marginTop: 8,
                                      borderTop: "1px dashed var(--border, #e2e6eb)",
                                      paddingTop: 8,
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, fontSize: 12, color: "var(--text-soft)" }}>Remark</div>
                                    <p style={{ margin: "2px 0 0 0", fontSize: 13, color: "var(--text-soft)" }}>{e.description}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {s.review_remarks && (
                        <div
                          className="muted"
                          style={{ fontSize: 12, marginTop: 6 }}
                        >
                          Review remarks: {s.review_remarks}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
